import type { LoadingType } from "./types";

export interface Footprint {
  lengthIn: number;
  widthIn: number;
}

export interface FloorSlot {
  alongIn: number;
  acrossIn: number;
  turned: boolean;
}

function slotSize(item: Footprint, turned: boolean) {
  return turned
    ? { depthIn: item.widthIn, spanIn: item.lengthIn }
    : { depthIn: item.lengthIn, spanIn: item.widthIn };
}

function gridSlots(
  truck: Footprint,
  item: Footprint,
  turned: boolean,
): Array<FloorSlot> {
  const { depthIn, spanIn } = slotSize(item, turned);
  const along = Math.floor(truck.lengthIn / depthIn);
  const across = Math.floor(truck.widthIn / spanIn);
  const slots: Array<FloorSlot> = [];
  for (let row = 0; row < along; row++) {
    for (let lane = 0; lane < across; lane++) {
      slots.push({
        alongIn: row * depthIn + depthIn / 2,
        acrossIn: lane * spanIn + spanIn / 2,
        turned,
      });
    }
  }
  return slots;
}

function alternatingRowSlots(
  truck: Footprint,
  item: Footprint,
  startTurned: boolean,
): Array<FloorSlot> {
  const slots: Array<FloorSlot> = [];
  let usedLengthIn = 0;
  let turned = startTurned;
  for (;;) {
    const { depthIn, spanIn } = slotSize(item, turned);
    const across = Math.floor(truck.widthIn / spanIn);
    if (across === 0 || usedLengthIn + depthIn > truck.lengthIn) break;
    for (let lane = 0; lane < across; lane++) {
      slots.push({
        alongIn: usedLengthIn + depthIn / 2,
        acrossIn: lane * spanIn + spanIn / 2,
        turned,
      });
    }
    usedLengthIn += depthIn;
    turned = !turned;
  }
  return slots;
}

function laneSlots(
  truck: Footprint,
  item: Footprint,
  turned: boolean,
  laneOffsetIn: number,
): Array<FloorSlot> {
  const { depthIn, spanIn } = slotSize(item, turned);
  const count = Math.floor(truck.lengthIn / depthIn);
  return Array.from({ length: count }, (_unused, row) => ({
    alongIn: row * depthIn + depthIn / 2,
    acrossIn: laneOffsetIn + spanIn / 2,
    turned,
  }));
}

function mixedLaneSlots(truck: Footprint, item: Footprint): Array<FloorSlot> {
  const straightPerLane = Math.floor(truck.lengthIn / item.lengthIn);
  const turnedPerLane = Math.floor(truck.lengthIn / item.widthIn);
  let best = { straightLanes: 0, turnedLanes: 0, count: 0 };
  for (
    let straightLanes = 1;
    straightLanes * item.widthIn + item.lengthIn <= truck.widthIn;
    straightLanes++
  ) {
    const turnedLanes = Math.floor(
      (truck.widthIn - straightLanes * item.widthIn) / item.lengthIn,
    );
    const count = straightLanes * straightPerLane + turnedLanes * turnedPerLane;
    if (count > best.count) best = { straightLanes, turnedLanes, count };
  }
  const slots: Array<FloorSlot> = [];
  for (let lane = 0; lane < best.straightLanes; lane++) {
    slots.push(...laneSlots(truck, item, false, lane * item.widthIn));
  }
  const turnedOffsetIn = best.straightLanes * item.widthIn;
  for (let lane = 0; lane < best.turnedLanes; lane++) {
    slots.push(
      ...laneSlots(truck, item, true, turnedOffsetIn + lane * item.lengthIn),
    );
  }
  return slots;
}

function pinwheelSlots(truck: Footprint, item: Footprint): Array<FloorSlot> {
  const straightAcross = Math.floor(truck.widthIn / item.widthIn);
  const turnedAcross = Math.floor(truck.widthIn / item.lengthIn);
  if (straightAcross === 0 || turnedAcross === 0) {
    const straight = gridSlots(truck, item, false);
    const turned = gridSlots(truck, item, true);
    return turned.length > straight.length ? turned : straight;
  }
  const candidates = [
    alternatingRowSlots(truck, item, false),
    alternatingRowSlots(truck, item, true),
    mixedLaneSlots(truck, item),
  ];
  return candidates.reduce((best, candidate) =>
    candidate.length > best.length ? candidate : best,
  );
}

function centerOnFloor(
  slots: Array<FloorSlot>,
  truck: Footprint,
  item: Footprint,
): Array<FloorSlot> {
  if (slots.length === 0) return slots;
  let maxAlongIn = 0;
  let maxAcrossIn = 0;
  for (const slot of slots) {
    const { depthIn, spanIn } = slotSize(item, slot.turned);
    maxAlongIn = Math.max(maxAlongIn, slot.alongIn + depthIn / 2);
    maxAcrossIn = Math.max(maxAcrossIn, slot.acrossIn + spanIn / 2);
  }
  const alongShiftIn = (truck.lengthIn - maxAlongIn) / 2;
  const acrossShiftIn = (truck.widthIn - maxAcrossIn) / 2;
  return slots
    .map((slot) => ({
      ...slot,
      alongIn: slot.alongIn + alongShiftIn,
      acrossIn: slot.acrossIn + acrossShiftIn,
    }))
    .sort(
      (first, second) =>
        first.alongIn - second.alongIn || first.acrossIn - second.acrossIn,
    );
}

function slotsFor(
  truck: Footprint,
  item: Footprint,
  loadingType: LoadingType,
): Array<FloorSlot> {
  switch (loadingType) {
    case "straight":
      return gridSlots(truck, item, false);
    case "turned":
      return gridSlots(truck, item, true);
    case "pinwheel":
      return pinwheelSlots(truck, item);
    default: {
      const unhandled: never = loadingType;
      throw new Error(`Unhandled loading type: ${String(unhandled)}`);
    }
  }
}

export function planFloor(
  truck: Footprint,
  item: Footprint,
  loadingType: LoadingType,
): Array<FloorSlot> {
  if (item.lengthIn <= 0 || item.widthIn <= 0) return [];
  return centerOnFloor(slotsFor(truck, item, loadingType), truck, item);
}
